'use client';

import { useState, useEffect, useCallback } from 'react';
import { Search } from 'lucide-react';
import VideoCard from '@/components/library/VideoCard';

interface Video {
  id: string;
  channel_id: string;
  title: string;
  thumbnail: string | null;
  duration: number | null;
  chunk_count: number;
  transcript_text: string | null;
}

interface Channel {
  id: string;
  name: string;
}

export default function LibraryPage() {
  const [videos, setVideos] = useState<Video[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [search, setSearch] = useState('');
  const [channelId, setChannelId] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchVideos = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (search) params.set('search', search);
    if (channelId) params.set('channelId', channelId);
    const res = await fetch(`/api/videos?${params}`);
    const data = await res.json() as { videos: Video[]; channels: Channel[]; total: number };
    setVideos(data.videos);
    setChannels(data.channels);
    setLoading(false);
  }, [search, channelId]);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  const channelName = (id: string) => channels.find(c => c.id === id)?.name ?? id;

  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <div className="fade-in mb-10">
        <h1
          style={{ color: 'var(--color-text)', letterSpacing: '0.2em' }}
          className="text-2xl font-light mb-1"
        >
          動画ライブラリ
        </h1>
        <p style={{ color: 'var(--color-muted)', letterSpacing: '0.08em' }} className="text-sm">
          インデックス済みの動画一覧
        </p>
      </div>

      {/* Filters */}
      <div className="fade-in fade-in-delay-1 flex flex-col sm:flex-row gap-4 mb-8">
        <div
          style={{ border: '1px solid var(--color-border)' }}
          className="flex items-center gap-2 flex-1 px-4 py-2.5 rounded-lg focus-within:border-[var(--color-accent)] transition-colors"
        >
          <Search size={14} style={{ color: 'var(--color-muted)' }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="動画タイトルで検索..."
            style={{ color: 'var(--color-text)' }}
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-[var(--color-muted)]"
          />
        </div>

        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setChannelId('')}
            style={{
              border: '1px solid var(--color-border)',
              backgroundColor: !channelId ? 'var(--color-accent)' : 'transparent',
              color: !channelId ? 'white' : 'var(--color-muted)',
            }}
            className="text-sm px-4 py-2 rounded-lg transition-all duration-200"
          >
            すべて
          </button>
          {channels.map(c => (
            <button
              key={c.id}
              onClick={() => setChannelId(c.id === channelId ? '' : c.id)}
              style={{
                border: '1px solid var(--color-border)',
                backgroundColor: channelId === c.id ? 'var(--color-accent)' : 'transparent',
                color: channelId === c.id ? 'white' : 'var(--color-muted)',
              }}
              className="text-sm px-4 py-2 rounded-lg transition-all duration-200"
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex justify-center py-20">
          <div
            style={{ borderColor: 'var(--color-border)', borderTopColor: 'var(--color-accent)' }}
            className="w-6 h-6 border-2 rounded-full animate-spin"
          />
        </div>
      ) : videos.length === 0 ? (
        <div className="text-center py-20">
          <p style={{ color: 'var(--color-muted)' }} className="text-sm">
            動画が見つかりません。管理画面から動画を取り込んでください。
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {videos.map((v, i) => (
            <div key={v.id} className="fade-in" style={{ animationDelay: `${i * 0.05}s` }}>
              <VideoCard
                id={v.id}
                title={v.title}
                channelName={channelName(v.channel_id)}
                thumbnail={v.thumbnail}
                duration={v.duration}
                chunkCount={v.chunk_count}
                hasTranscript={!!v.transcript_text}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
